// The frame of one of Neo's answers in the chat (TOM-709, the Marina chat
// spec): "✦ Neo" over it, the brief source line under its conclusion, and
// Copy and Helpful under it, small and secondary. The answer's words, its
// conclusion included, are drawn by NeoMarkdown in NeoChat.tsx; styles are in
// neo-answer.css.

import { useEffect, useRef, useState, type ReactNode } from 'react';

import { Icon } from '../ui/Icon';

/** "✦ Neo" over an answer. Screen readers hear the message's own "Neo:". */
export function NeoAnswerByline() {
  return (
    <p className="neo-answer__byline" aria-hidden="true">
      <svg viewBox="0 0 24 24" focusable="false">
        <path d="m12 2 2.5 7.5L22 12l-7.5 2.5L12 22l-2.5-7.5L2 12l7.5-2.5Z" />
      </svg>
      Neo
    </p>
  );
}

/** Where the answer's numbers came from, and when: "Boat readings · 14:20". */
export function NeoAnswerSourceLine({ text }: { text: string }) {
  return (
    <p className="neo-answer__source" data-testid="neo-answer-source">
      <Icon name="info" size={13} />
      <span>{text}</span>
    </p>
  );
}

const COPIED_MS = 2000;

/**
 * Copy, then Helpful (the thumbs, passed as children). Copy puts the answer's
 * words on the clipboard with each chart and card as its title, and says
 * "Copied" for a moment.
 */
export function NeoAnswerActions({
  copyText,
  sources,
  children,
}: {
  copyText: () => string;
  /**
   * Sources, where Behind the answer exists and the answer has a record to
   * show (TOM-710): opens it on this answer. `open` while it shows this one.
   */
  sources?: { open: boolean; onOpen: () => void };
  children?: ReactNode;
}) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (timer.current !== null) clearTimeout(timer.current);
    },
    []
  );

  function copy() {
    const text = copyText();
    if (text.length === 0 || navigator.clipboard === undefined) return;
    navigator.clipboard.writeText(text).then(
      () => {
        setCopied(true);
        if (timer.current !== null) clearTimeout(timer.current);
        timer.current = setTimeout(() => setCopied(false), COPIED_MS);
      },
      () => {
        // The browser refused the clipboard: nothing changed, nothing to say.
      }
    );
  }

  return (
    <div className="neo-answer__actions" data-testid="neo-answer-actions">
      <button
        type="button"
        className="neo-answer__action"
        aria-label={copied ? 'Copied' : 'Copy answer'}
        title={copied ? 'Copied' : 'Copy'}
        data-testid="neo-answer-copy"
        onClick={copy}
      >
        <Icon name={copied ? 'check' : 'copy'} size={16} />
      </button>
      <span className="sr-only" role="status">
        {copied ? 'Copied' : ''}
      </span>
      {children}
      {sources !== undefined ? (
        <button
          type="button"
          className={`neo-answer__sources${sources.open ? ' is-open' : ''}`}
          aria-expanded={sources.open}
          data-testid="neo-answer-sources"
          onClick={sources.onOpen}
        >
          <Icon name="info" size={14} />
          <span>Sources</span>
        </button>
      ) : null}
    </div>
  );
}
